/**
 * HandoffNotification Component
 * Notification bell for hot lead handoffs in the header
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, X, CheckCircle, XCircle } from 'lucide-react';
import { Button, useToast } from '@crisellevaldez/speculo-ui';
import { useHandoffQueue, useHandoffAlerts } from '../../../hooks/handoff';
import { HandoffQueueItem } from '../../../types/handoff.types';
import { cn } from '../../../utils/cn';
import { formatTimeAgo } from '../../../utils/handoffTime';
import { signalFlagsFrom } from '../../../utils/handoffFormat';
import { HandoffContactLine } from './HandoffContactLine';
import { HandoffMetaLine } from './HandoffMetaLine';
import { getHotnessBadge } from '../../../utils/handoffHotness';
import { InfoButton } from '../../common/InfoButton';
import { ClaimedLeadBanner } from './ClaimedLeadBanner';
import { HandoffActionModal } from './HandoffActionModal';

// Request notification permission on module load
if (
  typeof window !== 'undefined' &&
  'Notification' in window &&
  Notification.permission === 'default'
) {
  Notification.requestPermission();
}

// Card for a pending handoff in the dropdown. SPEC-2300: every row in the queue
// is claimable on the same terms for its whole 72-hour window, so there is no
// longer a second "extended window" variant with its own pair of actions.
interface HandoffCardProps {
  handoff: HandoffQueueItem;
  onClaim: (id: string) => void;
  onDecline: (id: string) => void;
  processingId: string | null;
  processingAction: ProcessingAction;
}

const HandoffCard: React.FC<HandoffCardProps> = ({
  handoff,
  onClaim,
  onDecline,
  processingId,
  processingAction,
}) => {
  const isThisProcessing = processingId === handoff.handoffId;
  const isClaiming = isThisProcessing && processingAction === 'claim';
  const isDeclining = isThisProcessing && processingAction === 'decline';
  const hotness = getHotnessBadge(handoff.hotnessLevel);
  const signalChips = signalFlagsFrom(handoff.signals, handoff.reasons);

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/40">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`/contact/${handoff.contactId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-foreground underline decoration-foreground/25 underline-offset-2 hover:text-primary hover:decoration-primary truncate max-w-[180px] text-left"
            >
              {handoff.contactName || '-'}
            </a>
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                hotness.bg,
                hotness.text,
                hotness.border
              )}
            >
              {hotness.icon}
              <span>{hotness.label}</span>
            </span>
          </div>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
          {formatTimeAgo(handoff.createdAt)}
        </span>
      </div>

      <HandoffContactLine
        phone={handoff.contactPhone}
        location={handoff.location}
        className="mb-2"
      />

      <HandoffMetaLine
        channel={handoff.channelOfOrigin}
        className="mb-3 mt-0.5"
      />

      {signalChips.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="mr-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Signals
          </span>
          {signalChips.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded-md border border-foreground/15 bg-foreground/[0.05] px-2 py-0.5 text-[11.5px] font-medium text-foreground/70"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => onClaim(handoff.handoffId)}
          disabled={isClaiming || isDeclining}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
        >
          <CheckCircle className="w-4 h-4 mr-1" />
          {isClaiming ? 'Claiming...' : 'Claim Lead'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDecline(handoff.handoffId)}
          disabled={isClaiming || isDeclining}
          className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-700/50 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
        >
          <XCircle className="w-4 h-4 mr-1" />
          {isDeclining ? 'Declining...' : 'Decline'}
        </Button>
      </div>
    </div>
  );
};

// Help content
const HandoffHelpContent: React.FC = () => (
  <div className="text-sm text-muted-foreground space-y-2 text-left bg-muted/50 rounded-lg p-4">
    <p>
      <strong>🔔 What are handoffs?</strong>
      <br />
      When a lead becomes "hot" (ready to convert), Remi (AI) will hand off control to you.
    </p>
    <p>
      <strong>✅ Claim</strong> — Take control of the lead and contact them directly. Remi will
      pause automated follow-ups.
    </p>
    <p>
      <strong>❌ Decline</strong> — Let Remi continue nurturing the lead automatically.
    </p>
    <p>
      <strong>⏱️ 72-hour window</strong> — A handoff stays claimable for 72 hours. Remi keeps
      nurturing the lead the whole time; after 72 hours the handoff expires and drops off this list.
    </p>
  </div>
);

interface ClaimedLead {
  contactId: string;
  contactName: string | null;
  handoffId: string;
}

type ProcessingAction = 'claim' | 'decline' | null;


export const HandoffNotification: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // After a successful claim, drop the agent straight on the contact's page so
  // they can act on the lead they just took. Mirrors the EditContactModal
  // redirect the dashboard's Handoffs modal does.
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [claimedLeads, setClaimedLeads] = useState<ClaimedLead[]>([]);
  // Only decline confirms. Claiming is reversible — the agent can give the lead
  // back — so it goes through on the first tap.
  const [declineTarget, setDeclineTarget] = useState<HandoffQueueItem | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<ProcessingAction>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  const { pendingHandoffs, isLoading, claim, decline, refetch } = useHandoffQueue({
    enabled: true,
    refetchInterval: 30000,
  });

  // Open dropdown if URL has openHandoffQueue param
  useEffect(() => {
    if (searchParams.get('openHandoffQueue') === 'true') {
      setIsOpen(true);
      // Remove the param from URL after opening
      searchParams.delete('openHandoffQueue');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Real-time alerts
  useHandoffAlerts({
    enabled: true,
    onNewHandoff: (handoff) => {
      document.title = `🔥 Hot Lead! - Speculo`;
      if (Notification.permission === 'granted') {
        new Notification('🔥 Hot Lead!', {
          body: `${handoff.contactName || 'New lead'} is ready for contact`,
          icon: '/speculo-icon.png',
        });
      }
      const audio = new Audio('/sounds/notif.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => console.log('[Handoff] Audio blocked'));
      refetch();
    },
    onClaimed: refetch,
    onDeclined: refetch,
  });

  // Close on an outside click rather than on blur. Blur cannot work here: the
  // action modal renders in a portal, so opening it moves focus out of the
  // dropdown for good — the dropdown would collapse behind the modal, and once
  // focus had left, no later blur would ever fire to close it again.
  //
  // While the modal is open it owns the interaction, so clicks and Escape are
  // ignored here (a click inside a portalled modal is "outside" this element).
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (declineTarget) return;
      const root = dropdownRef.current;
      if (root && !root.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (declineTarget) return;
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, declineTarget]);

  const handleClaim = async (id: string, notes?: string) => {
    const handoff = pendingHandoffs.find((h) => h.handoffId === id);
    setProcessingId(id);
    setProcessingAction('claim');
    try {
      const result = await claim(id, notes);
      if (result.success) {
        // Add to claimed leads if we have the handoff info locally
        if (handoff) {
          setClaimedLeads((prev) => [
            ...prev,
            { contactId: handoff.contactId, contactName: handoff.contactName, handoffId: id },
          ]);
        }
        setDeclineTarget(null);
        showToast({ message: 'Handoff claimed successfully!' });
        if (handoff?.contactId) {
          setIsOpen(false);
          navigate(`/contact/${handoff.contactId}`);
        }
      } else {
        showToast({ message: result.error || 'Failed to claim. Try again.' });
      }
    } catch (e) {
      console.error('Error claiming:', e);
      showToast({ message: 'Failed to claim. Something went wrong.' });
    } finally {
      setProcessingId(null);
      setProcessingAction(null);
    }
  };
  const handleDecline = async (id: string) => {
    setProcessingId(id);
    setProcessingAction('decline');
    try {
      const result = await decline(id, 'Declined');
      if (result.success) {
        setDeclineTarget(null);
        showToast({ message: 'Handoff declined. Remi will continue nurturing.' });
      } else {
        showToast({ message: result.error || 'Failed to decline. Try again.' });
      }
    } catch (e) {
      console.error('Error declining:', e);
      showToast({ message: 'Failed to decline. Something went wrong.' });
    } finally {
      setProcessingId(null);
      setProcessingAction(null);
    }
  };
  // The queue endpoint already returns only rows still inside their 72-hour
  // claim window — hard-expired rows drop out server-side.
  const pendingCount = pendingHandoffs.length;
  const hasUrgent = pendingHandoffs.some((h) => h.hotnessLevel === 'urgent');

  if (pendingCount > 0) document.title = `(${pendingCount}) 🔥 Speculo`;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12.5px] transition-colors mouse-glow',
          isOpen ? 'header-nav-active' : 'header-nav'
        )}
        aria-label={`Handoff notifications: ${pendingCount} pending`}
      >
        <Bell className="w-4 h-4" />
        <span className="hidden md:inline">Handoffs</span>
        {pendingCount > 0 && (
          <span
            className={cn(
              'min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold rounded-full px-1',
              hasUrgent ? 'bg-red-500 text-white animate-shake' : 'bg-orange-500 text-white'
            )}
          >
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed sm:absolute inset-0 sm:inset-auto sm:top-full sm:left-auto sm:right-0 sm:mt-2 w-full sm:w-[520px] h-full sm:h-auto sm:max-h-[calc(100vh-80px)] bg-card sm:rounded-lg shadow-xl border-0 sm:border sm:border-border z-50 overflow-hidden animate-in slide-in-from-bottom sm:slide-in-from-right-5 duration-200 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 bg-muted/40 border-b border-border shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="min-w-0">
                <h3 className="font-semibold text-foreground text-sm sm:text-base">
                  Pending Handoffs
                </h3>
                <p className="text-xs text-muted-foreground truncate">Hot leads waiting for you</p>
              </div>
              <InfoButton
                variant="help"
                isActive={showHelp}
                onClick={() => setShowHelp(!showHelp)}
                title="What are handoffs?"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {pendingCount > 0 && (
                <span className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400 hidden xs:inline">
                  {pendingCount} pending
                </span>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 sm:p-1 hover:bg-muted rounded"
              >
                <X className="w-5 h-5 sm:w-4 sm:h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Claimed Lead Banners */}
          <div className="shrink-0">
            {claimedLeads.map((lead) => (
              <ClaimedLeadBanner
                key={lead.handoffId}
                contactId={lead.contactId}
                contactName={lead.contactName}
                onDismiss={() =>
                  setClaimedLeads((prev) => prev.filter((l) => l.handoffId !== lead.handoffId))
                }
                onViewContact={() => setIsOpen(false)}
              />
            ))}
          </div>

          {/* Content - Actionable handoffs (pending + extended window) */}
          <div className="flex-1 overflow-y-auto scrollbar-custom min-h-0 p-3 bg-muted">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : pendingHandoffs.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-10 h-10 sm:w-12 sm:h-12 text-foreground/20 mx-auto mb-3" />
                <p className="text-sm sm:text-base text-muted-foreground">No pending handoffs</p>
                <p className="text-xs sm:text-sm text-muted-foreground/80 mt-1">
                  When hot leads arrive, they'll appear here
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {pendingHandoffs.map((h) => (
                  <HandoffCard
                    key={h.handoffId}
                    handoff={h}
                    onClaim={() => handleClaim(h.handoffId)}
                    onDecline={() => setDeclineTarget(h)}
                    processingId={processingId}
                    processingAction={processingAction}
                  />
                ))}
              </div>
            )}
            {(showHelp || pendingHandoffs.length === 0) && (
              <div className="p-4 border-t border-border bg-card">
                <HandoffHelpContent />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Keyed per target so the notes box is a fresh instance every time it
          opens. The modal stays mounted, and the success paths close it by
          clearing the target rather than through its own close handler — so
          without this the previous contact's note is still sitting there for
          the next one, ready to be submitted onto the wrong lead. */}
      <HandoffActionModal
        key={declineTarget?.handoffId ?? 'closed'}
        isOpen={declineTarget !== null}
        contactName={declineTarget?.contactName || 'this lead'}
        contactPhone={declineTarget?.contactPhone}
        isSubmitting={processingId === declineTarget?.handoffId}
        onClose={() => setDeclineTarget(null)}
        onConfirm={() => {
          if (declineTarget) handleDecline(declineTarget.handoffId);
        }}
      />
    </div>
  );
};

export default HandoffNotification;
